import React, { useRef } from 'react';
import { Download, Printer } from 'lucide-react';
import html2pdf from 'html2pdf.js';
import { useLanguage } from '../context/LanguageContext';

const PlasticBanResolutionForm = () => {
  const { language } = useLanguage();
  const componentRef = useRef();

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPdf = () => {
    const element = componentRef.current;
    const opt = {
      margin: 0,
      filename: language === 'marathi' ? 'Plastic_Ban_Resolution.pdf' : 'Plastic_Ban_Resolution_EN.pdf',
      image: { type: 'jpeg', quality: 1 },
      html2canvas: { scale: 3, useCORS: true, logging: false },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
    };
    html2pdf().from(element).set(opt).save();
  };

  return (
    <div className="min-h-screen bg-gray-200 py-8 font-sans print:py-0 print:bg-white flex flex-col items-center">
      <style>
        {`
          .page {
            width: 210mm;
            height: 297mm;
            padding: 20mm;
            margin: 0 auto 20px auto;
            box-sizing: border-box;
            position: relative;
            background-color: white;
            overflow: hidden;
            box-shadow: 0 0 15px rgba(0,0,0,0.1);
          }
          
          .dashed-line {
            border-bottom: 1px dashed black;
            display: inline-block;
          }

          @media print {
            @page {
              size: A4 portrait;
              margin: 0mm !important;
            }
            html, body {
              margin: 0 !important;
              padding: 0 !important;
              width: 210mm !important;
              height: 297mm !important;
              background-color: white !important;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            .page {
              margin: 0 !important;
              box-shadow: none !important;
              width: 210mm !important;
              height: 297mm !important;
              page-break-after: always !important;
              break-after: page !important;
              break-inside: avoid !important;
              border: none !important;
            }
            .no-print {
              display: none !important;
            }
            .min-h-screen {
              min-height: auto !important;
              padding: 0 !important;
            }
          }
        `}
      </style>
      
      {/* Controls Container */}
      <div className="w-full max-w-[210mm] mb-6 flex justify-end space-x-4 print:hidden px-4 no-print">
        <button
          onClick={handlePrint}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 shadow-sm transition-colors"
        >
          <Printer className="w-4 h-4 mr-2" />
          {language === 'marathi' ? 'Print' : 'Print'}
        </button>
        <button
          onClick={handleDownloadPdf}
          className="flex items-center px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 shadow-sm transition-colors"
        >
          <Download className="w-4 h-4 mr-2" />
          {language === 'marathi' ? 'Download PDF' : 'Download PDF'}
        </button>
      </div>

      <div ref={componentRef} className="flex flex-col items-center text-black">
        {/* PAGE 1 */}
        <div className="bg-white relative box-border flex flex-col page">
           <div className="flex justify-center items-center text-[18px] font-bold mb-4">
              <span>{language === 'marathi' ? 'कार्यालय ग्रामपंचायत' : 'Gram Panchayat Office'}</span>
              <span className="dashed-line w-40 h-4 mx-2"></span>
              <span>{language === 'marathi' ? 'ता.' : 'Tal.'}</span>
              <span className="dashed-line w-32 h-4 mx-2"></span>
              <span>{language === 'marathi' ? 'जि.' : 'Dist.'}</span>
              <span className="dashed-line w-32 h-4 mx-2"></span>
           </div>

           <div className="text-center text-[16px] mb-6">
              <span>{language === 'marathi' ? 'दिनांक:-' : 'Date:-'}</span>
              <span className="dashed-line w-8 h-4 mx-1"></span>
              <span>/</span>
              <span className="dashed-line w-8 h-4 mx-1"></span>
              <span>{language === 'marathi' ? '/२०' : '/20'}</span>
              <span className="ml-2 font-bold text-[18px]">{language === 'marathi' ? 'रोजी झालेल्या मासिक/ग्राम सभा बैठकीचे इतिवृत्त नोंदवही' : 'Minutes Register of Monthly/Gram Sabhas Meeting held on'}</span>
           </div>

           <table className="w-full border-collapse border border-black text-[14px] mb-6">
              <thead>
                 <tr>
                    <th className="border border-black p-2 w-16 text-center">{language === 'marathi' ? 'विषय नंबर' : 'Subject No.'}</th>
                    <th className="border border-black p-2 w-16 text-center">{language === 'marathi' ? 'ठराव नंबर' : 'Resolution No.'}</th>
                    <th className="border border-black p-4 text-center text-[16px]">{language === 'marathi' ? 'सभेपुढे विचाराकरीता आलेले विषय व ठराव' : 'Subjects and Resolutions brought before the meeting for consideration'}</th>
                    <th className="border border-black p-2 w-48 text-center">{language === 'marathi' ? 'शेरे आणि तारखेसह ठरावाची अंमल- बजावणी' : 'Implementation of resolution with remarks and date'}</th>
                 </tr>
              </thead>
              <tbody className="min-h-[100px]">
                 <tr>
                    <td className="border border-black p-2 h-12"></td>
                    <td className="border border-black p-2 h-12"></td>
                    <td className="border border-black p-2 h-12"></td>
                    <td className="border border-black p-2 h-12"></td>
                 </tr>
              </tbody>
           </table>

           <div className="text-[15px] space-y-4 leading-relaxed">
              <p className="flex items-end flex-wrap">
                 <span>{language === 'marathi' ? 'आज दिनांक' : 'Today Date'}</span>
                 <span className="dashed-line w-10 h-4 mx-1"></span>
                 <span>/</span>
                 <span className="dashed-line w-10 h-4 mx-1"></span>
                 <span>{language === 'marathi' ? '/२० रोजी ग्रामपंचायत कार्यालय' : '/20 at Gram Panchayat Office'}</span>
                 <span className="dashed-line flex-grow h-4 mx-1"></span>
                 <span>{language === 'marathi' ? 'ची मासिक/ ग्राम सभा सरपंच श्री./सौ.' : "'s Monthly/Gram Sabha Sarpanch Mr./Mrs."}</span>
                 <span className="dashed-line w-full h-4 mt-2"></span>
                 <span className="mt-1">{language === 'marathi' ? 'यांच्या अध्यक्षतेखाली ग्रामपंचायत कार्यालय येथे सकाळी ठीक : वाजता बोलविण्यात आली.' : 'was convened under the chairmanship of, at Gram Panchayat Office exactly at : AM/PM.'}</span>
              </p>

              <p className="font-bold">{language === 'marathi' ? 'या सभेत खालील ठराव मांडण्यात आला.' : 'The following resolution was moved in this meeting.'}</p>

              <p className="text-[17px]">
                 <span className="font-bold">{language === 'marathi' ? 'विषय क्र. : ग्रामपंचायत कार्यक्षेत्रात प्लॅस्टिक वापरांवर बंदी करणे व अंमलबजावणी करणेबाबत.' : 'Subject No. : Regarding ban on use of plastic and implementation in Gram Panchayat jurisdiction.'}</span>
              </p>

              <p className="text-justify indent-12">
                 {language === 'marathi' ? 'सुचक यांनी सुचविले की, महाराष्ट्र शासनाच्या वतीने हाती घेण्यात आलेल्या प्लॅस्टिक पिशवी आणि थर्माकोल मुक्त महाराष्ट्र या कार्यक्रमाद्वारे दि.२३/०३/२०१८ पासून संपूर्ण राज्यात प्लॅस्टिक वापरांवर बंदी घालण्यात आलेली आहे. यामध्ये प्रामुख्याने खालील बाबींचा समावेश होतो.' : 'The proposer suggested that, through the Plastic Bag and Thermocol Free Maharashtra program undertaken on behalf of the Government of Maharashtra, the use of plastic has been banned in the entire state from 23/03/2018. This mainly includes the following items.'}
              </p>

              <div className="ml-8 space-y-2">
                 <p>{language === 'marathi' ? '१. सर्व प्रकारच्या प्लॅस्टिक पिशव्या.' : '1. All types of plastic bags.'}</p>
                 <p>{language === 'marathi' ? '२. थर्माकोल व प्लॅस्टिकपासून बनविण्यात येणा-या व एकदाच वापरल्या जाणा-या डिस्पोजेबल वस्तू ( ताट, कप्स, प्लेटस, ग्लास, वाटी, चमचे इ. )' : '2. Disposable items made of thermocol and plastic and used only once (plates, cups, plates, glasses, bowls, spoons, etc.)'}</p>
                 <p>{language === 'marathi' ? '३. हॉटेलमध्ये अन्नपदार्थ पदार्थ पॅकेजींगसाठी वापरले जाणारे प्लॅस्टिक भांडी व वाटी' : '3. Plastic utensils and bowls used for food packaging in hotels'}</p>
                 <p>{language === 'marathi' ? '४. द्रव पदार्थ साठवण्यासाठी वापरत येणारे प्लॅस्टिक पाऊच / कप.' : '4. Plastic pouches/cups used to store liquids.'}</p>
                 <p>{language === 'marathi' ? '५. ५० मायक्रॉन पेक्षा कमी जाडीची प्लॅस्टिक पिशवी.' : '5. Plastic bag less than 50 microns thick.'}</p>
              </div>

              <p className="text-justify">
                 {language === 'marathi' ? 'वरील बंदी असलेले प्लॅस्टिक पिशव्या उत्पादन वापरणा-यास ५,०००/- रु. दंड दुस-यांदा १०,०००/- रु. दंड आणि तिस-यांदा गुन्हा केला तर २५,०००/- रु. दंड आणि तीन महिन्यांपर्यंत शिक्षेची तरतूद आहे.' : 'There is a provision of a fine of Rs. 5,000/- for a person producing/using the above banned plastic bags, a fine of Rs. 10,000/- for the second time, and if the offense is committed for the third time, a fine of Rs. 25,000/- and imprisonment up to three months.'}
              </p>

              <p className="text-justify indent-12">
                 {language === 'marathi' ? 'आपल्या ग्रामपंचायत कार्यक्षेत्रात प्लॅस्टिक वापरांवर बंदी करणे व अंमलबजावणी करण्यात यावी, तसेच आवश्यक जनजागृती करण्यात यावी .गावातील सर्व व्यावसायिकांनी प्लॅस्टिक पिशव्यां ऐवजी कापडी व कागदी पिशव्यांचा वापर करावा. आणि प्लॅस्टिकचा वापर टाळावा व गाव प्लॅस्टिकमुक्त करण्यास हातभार लावावा. त्यास अनुमोदक यांनी अनुमोदन दिले. सभेत विचार विनिमय करून वरीलप्रमाणे निर्णय घेण्यात आला व ठराव सर्वानुमते/बहुमताने संमत करण्यात आला.' : 'Ban on the use of plastic and its implementation should be done in our Gram Panchayat jurisdiction, as well as necessary public awareness should be created. All businessmen in the village should use cloth and paper bags instead of plastic bags, avoid the use of plastic and help make the village plastic-free. The seconder seconded it. After deliberation in the meeting, the above decision was taken and the resolution was passed unanimously/by majority.'}
              </p>

              <div className="mt-8 space-y-4">
                 <p className="flex items-end">
                    <span className="mr-2">{language === 'marathi' ? 'सुचक: श्री. सौ.' : 'Proposer: Mr./Mrs.'}</span>
                    <span className="dashed-line flex-grow h-4"></span>
                 </p>
                 <p className="flex items-end">
                    <span className="mr-2">{language === 'marathi' ? 'अनुमोदन: श्री./सौ.' : 'Seconder: Mr./Mrs.'}</span>
                    <span className="dashed-line flex-grow h-4"></span>
                 </p>
              </div>

              <div className="flex justify-end mt-12">
                 <div className="flex flex-col items-center w-[400px] space-y-1">
                    <div className="flex justify-between w-full px-4 mb-1">
                       <p className="font-bold">{language === 'marathi' ? 'ग्रामपंचायत अधिकारी' : 'Gram Panchayat Officer'}</p>
                       <p className="font-bold">{language === 'marathi' ? 'सरपंच' : 'Sarpanch'}</p>
                    </div>
                    <span className="dashed-line w-full h-4"></span>
                 </div>
              </div>
           </div>

           <div className="absolute bottom-8 left-14 text-blue-700 text-sm underline print:hidden">
             www.majhigrampanchayat.com
           </div>
        </div>
      </div>
    </div>
  );
};

export default PlasticBanResolutionForm;
